import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { UtilisateurExterne } from '@/services/api/types';
import { store } from '@/store';
import { nextTick } from 'vue';
import { router } from '@/router';
class AuthService {
  private static readonly TYPE_CODE_PDS_DOCTEUR = ['01', '06'];
  private static readonly TYPE_CODE_PDS_OFFICINE = '02';

  /**
   * Vérifie si un utilisateur est connecté
   */
  static isLoggedIn(): boolean {
    // Récupération du token Jwt
    const accessToken: string | null = this.getAuthToken();

    if (!accessToken) {
      return false;
    }
    // Signature jeton validé côté serveur avec lexik/jwt-authentication-bundle
    const decodedToken: any = jwt_decode(accessToken);
    // Extraction de la date d'expiration
    const expirationDate = new Date(decodedToken.exp * 1000);

    if (expirationDate < new Date()) {
      // Redirection vers la page d'inactivité après la suppression de la session
      store.dispatch('authentication/logout').then(() => {
        nextTick(() => {
          router
            .push({ name: 'erreurG', query: { errorCode: '401' } })
            .catch((error) => {
              if (error.name !== 'NavigationDuplicated') {
                return;
              }
            });
        });
      });
    }
    return true;
  }

  /**
   * Récupère le token JWT stocké dans le localStorage
   * @returns {string|null}
   */
  static getAuthToken(): string | null {
    return localStorage.getItem('utilisateurExterneToken');
  }

  /**
   * Vérifie si les identifiants de connexion sont valides et met à jour le local storage avec le jeton jwt
   * @param login Le nom d'utilisateur.
   * @param password Le mot de passe.
   * @returns retourne true en cas de succès.
   */
  static async checkIdentifiants(
    login: string,
    password: string
  ): Promise<boolean | string> {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}login_check`,
        {
          login: login,
          password: password
        }
      );

      if (response.data.token && response.status === 200) {
        localStorage.setItem('utilisateurExterneToken', response.data.token);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (
          typeof error.response.data === 'object' &&
          error.response.data !== null &&
          'message' in error.response.data
        ) {
          return (
            (error.response.data as { message: string }).message ||
            'Une erreur est survenue'
          );
        }
      }
      return false;
    }
  }

  /**
   * Authentification via le jeton PSBEN 2
   */ static async getPsBenUserFromApi(psbenToken: string): Promise<boolean> {
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}psben/`, {
        params: {
          psben: psbenToken
        }
      });
      if (response.data.token && response.status === 200) {
        localStorage.setItem('utilisateurExterneToken', response.data.token);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  static removeToken(): void {
    localStorage.removeItem('utilisateurExterneToken');
  }

  static gereProfils(userInfo: UtilisateurExterne): UtilisateurExterne {
    // Gestion délégataire
    if (userInfo.delegataire) {
      const typeCodePds = userInfo.delegataire.typeCodePds;
      userInfo.isDocteur =
        AuthService.TYPE_CODE_PDS_DOCTEUR.includes(typeCodePds);
      userInfo.isOfficine = typeCodePds === AuthService.TYPE_CODE_PDS_OFFICINE;
    }

    // Peut valider si exécutant ou délégataire nommé
    if (userInfo.isExecutant || userInfo.isDelegataire) {
      userInfo.peutValider = true;
    }

    return userInfo;
  }

  /**
   * Récupère les informations de l'utilisateur depuis le stockage local
   * @returns Les informations de l'utilisateur ou null si non trouvées
   */
  static getUserInfo(): UtilisateurExterne | null {
    const utilisateurInfo =
      store.getters['authentication/utilisateurExterneInfo'];
    return utilisateurInfo || null;
  }
}
export default AuthService;
